/**
 * Pure computation functions for CoursePreview schematic rendering.
 * No React dependency — fully testable.
 */

const DEG_TO_RAD = Math.PI / 180;

/** Perpendicular offset in pixels for repeated segments. */
const OFFSET_PX = 6;

/** Mark circle radius in pixels. */
export const MARK_RADIUS = 14;

/** Arc stroke width in pixels. */
export const ARC_STROKE = 2.5;

/** Center-to-center distance between concentric rounding rings. */
const RING_STEP = ARC_STROKE * 2;

/**
 * Project marks from geographic coords to SVG pixel coords.
 * Equirectangular projection with cosine correction.
 *
 * @param {Array<{id:string, latitude:number, longitude:number}>} marks
 * @param {string[]} markIds - ordered course sequence
 * @param {number} width - SVG width
 * @param {number} height - SVG height
 * @param {number} [padding=40] - padding around edges
 * @returns {{ positions: Object<string,{x:number,y:number}>, sequencePositions: {x:number,y:number}[] }}
 */
/**
 * Resolve each course markId to its mark's canonical id, matching case-insensitively.
 *
 * Courses saved via CourseEditor store their markIds lowercased
 * (`markIds.map((id) => id.toLowerCase())`), but marks are keyed by their canonical
 * (usually uppercase) id. Every mark lookup downstream (projectMarks, markLookup,
 * positions, distance) keys by `mark.id`, so a course carrying lowercased ids fails
 * every lookup → empty positions → "No marks with coordinates" (ra-or1y, P0 found by
 * Chris via the 3-render trace: markIds came lowercase while positions stayed uppercase).
 *
 * Folding case to the marks' actual id at the single point markIds enter the preview
 * fixes it for case drift in either direction. Unknown ids (no matching mark) pass
 * through unchanged, so behaviour is identical when cases already agree.
 * @param {Array} markIds
 * @param {Array} marks
 * @returns {Array}
 */
export function canonicalizeMarkIds(markIds, marks) {
  const arr = Array.isArray(markIds) ? markIds : [];
  const canon = new Map();
  for (const m of Array.isArray(marks) ? marks : []) {
    // First-wins: when two marks collide on the case-folded key (e.g. a club 'N'
    // and a bundle 'n'), keep the first — marks are ordered club-first, so this
    // preserves the "club marks take priority" contract instead of last-write-wins
    // letting a bundle/stale mark override the club one. (ra-or1y, codex review.)
    if (m && m.id != null) {
      const key = String(m.id).toUpperCase();
      if (!canon.has(key)) canon.set(key, m.id);
    }
  }
  return arr.map((id) => canon.get(String(id).toUpperCase()) ?? id);
}

export function projectMarks(marks, markIds, width, height, padding = 40) {
  if (!Array.isArray(markIds) || markIds.length === 0) {
    return { positions: {}, sequencePositions: [] };
  }

  const lookup = {};
  for (const m of marks) {
    if (typeof m.latitude === 'number' && typeof m.longitude === 'number' &&
        !(m.latitude === 0 && m.longitude === 0)) {
      lookup[m.id] = m;
    }
  }

  // Collect unique marks that appear in the course and have coords
  const seen = new Set();
  const uniqueMarks = [];
  for (const id of markIds) {
    if (!seen.has(id) && lookup[id]) {
      seen.add(id);
      uniqueMarks.push(lookup[id]);
    }
  }

  if (uniqueMarks.length === 0) {
    return { positions: {}, sequencePositions: [] };
  }

  if (uniqueMarks.length === 1) {
    const center = { x: width / 2, y: height / 2 };
    const positions = { [uniqueMarks[0].id]: center };
    const sequencePositions = markIds
      .filter((id) => positions[id])
      .map((id) => positions[id]);
    return { positions, sequencePositions };
  }

  // Compute mid-latitude for cosine correction
  const sumLat = uniqueMarks.reduce((s, m) => s + m.latitude, 0);
  const midLat = sumLat / uniqueMarks.length;
  const cosLat = Math.cos(midLat * DEG_TO_RAD);

  const refLat = uniqueMarks[0].latitude;
  const refLon = uniqueMarks[0].longitude;

  // Project to nautical-mile-like coords
  const projected = {};
  for (const m of uniqueMarks) {
    projected[m.id] = {
      x: (m.longitude - refLon) * 60 * cosLat,
      y: -(m.latitude - refLat) * 60, // flip Y for screen coords
    };
  }

  // Find bounds
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of Object.values(projected)) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const rangeX = maxX - minX;
  const rangeY = maxY - minY;
  const drawW = width - padding * 2;
  const drawH = height - padding * 2;

  let scale;
  if (rangeX === 0 && rangeY === 0) {
    scale = 1;
  } else if (rangeX === 0) {
    scale = drawH / rangeY;
  } else if (rangeY === 0) {
    scale = drawW / rangeX;
  } else {
    scale = Math.min(drawW / rangeX, drawH / rangeY);
  }

  const scaledW = rangeX * scale;
  const scaledH = rangeY * scale;
  const offsetX = padding + (drawW - scaledW) / 2;
  const offsetY = padding + (drawH - scaledH) / 2;

  const positions = {};
  for (const [id, p] of Object.entries(projected)) {
    positions[id] = {
      x: (p.x - minX) * scale + offsetX,
      y: (p.y - minY) * scale + offsetY,
    };
  }

  const sequencePositions = markIds
    .filter((id) => positions[id])
    .map((id) => positions[id]);

  return { positions, sequencePositions, transform: { scale, offsetX, offsetY, minX, minY, refLat, refLon, cosLat } };
}

/**
 * Project a single geographic point using a transform from projectMarks.
 *
 * @param {number} lat
 * @param {number} lon
 * @param {Object} transform - transform object from projectMarks
 * @returns {{x:number,y:number}}
 */
export function projectGeoPoint(lat, lon, transform) {
  const { scale, offsetX, offsetY, minX, minY, refLat, refLon, cosLat } = transform;
  const px = (lon - refLon) * 60 * cosLat;
  const py = -(lat - refLat) * 60;
  return {
    x: (px - minX) * scale + offsetX,
    y: (py - minY) * scale + offsetY,
  };
}

/**
 * Compute the turn angle at a mark given incoming and outgoing bearings.
 * Returns degrees in range (-180, +180].
 * Positive = clockwise turn (leave to port).
 * Negative = counter-clockwise turn (leave to starboard).
 *
 * @param {number} inBearing - bearing of incoming leg (degrees, 0-360)
 * @param {number} outBearing - bearing of outgoing leg (degrees, 0-360)
 * @returns {number} turn angle in degrees
 */
export function computeTurnAngle(inBearing, outBearing) {
  let diff = outBearing - inBearing;
  while (diff > 180) diff -= 360;
  while (diff <= -180) diff += 360;
  return diff;
}

/**
 * Returns CSS color for a turn angle (absolute value).
 * Neutral slate for normal angles, amber for unusual (passing mark / near-360).
 *
 * @param {number} absDeg - absolute turn angle in degrees
 * @returns {string} CSS color
 */
export function turnAngleColor(absDeg) {
  if (absDeg < 15 || absDeg > 165) return '#d97706'; // amber — passing mark or 360
  return '#475569'; // slate — normal
}

/**
 * Compute perpendicular offsets for repeated segments in a course.
 * Each segment group gets centered offsets.
 *
 * @param {string[]} markIds - course mark sequence
 * @returns {number[]} offset in pixels per leg (length = markIds.length - 1)
 */
export function computeSegmentOffsets(markIds) {
  if (!Array.isArray(markIds) || markIds.length < 2) return [];

  // Group legs by normalized segment key
  const groups = {};
  for (let i = 0; i < markIds.length - 1; i++) {
    const a = markIds[i];
    const b = markIds[i + 1];
    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(i);
  }

  const offsets = new Array(markIds.length - 1).fill(0);
  for (const indices of Object.values(groups)) {
    const n = indices.length;
    if (n <= 1) continue;
    indices.forEach((legIdx, rank) => {
      offsets[legIdx] = (rank - (n - 1) / 2) * OFFSET_PX;
    });
  }

  return offsets;
}

/**
 * Offset a line segment perpendicular to its direction.
 *
 * @param {{x:number,y:number}} p1
 * @param {{x:number,y:number}} p2
 * @param {number} offsetPx - perpendicular offset (positive = right side)
 * @returns {[{x:number,y:number}, {x:number,y:number}]}
 */
export function offsetLinePoints(p1, p2, offsetPx) {
  if (offsetPx === 0) return [p1, p2];

  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return [p1, p2];

  // Perpendicular unit vector (rotated 90° clockwise)
  const nx = dy / len;
  const ny = -dx / len;

  return [
    { x: p1.x + nx * offsetPx, y: p1.y + ny * offsetPx },
    { x: p2.x + nx * offsetPx, y: p2.y + ny * offsetPx },
  ];
}

// =========================================================================
// Belt-tangent mode (new)
// =========================================================================

/**
 * Compute the radius of a rounding ring for a given visit number.
 * Rings are concentric to the mark center with a gap equal to ARC_STROKE between edges.
 *
 * @param {number} visitNumber - 1-based visit count
 * @returns {number} radius in pixels
 */
export function ringRadius(visitNumber) {
  return MARK_RADIUS + visitNumber * RING_STEP;
}

/**
 * Compute the belt tangent point where a leg line meets a rounding circle.
 * Like a belt around a pulley — the line arrives/departs at 90° to the radius.
 *
 * In SVG y-down coords: increasing angle = clockwise on screen.
 * Port rounding = clockwise, starboard = counter-clockwise.
 *
 * @param {{x:number,y:number}} markPos - center of the rounding circle
 * @param {{x:number,y:number}} otherPos - the other end of the leg
 * @param {number} radius - radius of the rounding circle
 * @param {boolean} isPort - true for port (clockwise on screen)
 * @param {boolean} isArrival - true if arriving at mark, false if departing
 * @returns {{point:{x:number,y:number}, angle:number}}
 */
export function beltTangent(markPos, otherPos, radius, isPort, isArrival) {
  const dx = otherPos.x - markPos.x;
  const dy = otherPos.y - markPos.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // If too close for a true tangent, fall back to nearest point on circle
  if (dist <= radius) {
    const angle = Math.atan2(dy, dx);
    return {
      point: { x: markPos.x + radius * Math.cos(angle), y: markPos.y + radius * Math.sin(angle) },
      angle,
    };
  }

  const theta = Math.atan2(dy, dx); // angle from mark center toward other mark
  const gamma = Math.acos(radius / dist); // tangent offset angle

  let tangentAngle;
  if (isPort) {
    tangentAngle = isArrival ? theta - gamma : theta + gamma;
  } else {
    tangentAngle = isArrival ? theta + gamma : theta - gamma;
  }

  return {
    point: {
      x: markPos.x + radius * Math.cos(tangentAngle),
      y: markPos.y + radius * Math.sin(tangentAngle),
    },
    angle: tangentAngle,
  };
}

/**
 * Generate an SVG path for a rounding arc between two angles on a circle.
 *
 * @param {{x:number,y:number}} center - circle center
 * @param {number} radius - circle radius
 * @param {number} startAngle - start angle in radians (arrival tangent)
 * @param {number} endAngle - end angle in radians (departure tangent)
 * @param {boolean} isPort - true for port (clockwise), false for starboard
 * @param {number} [sweepFraction=1] - fraction of arc to draw (0-1)
 * @returns {string} SVG path d attribute
 */
export function beltArcPath(center, radius, startAngle, endAngle, isPort, sweepFraction = 1) {
  let sweep = endAngle - startAngle;

  // Port = leave mark to port = boat turns clockwise = arc goes counter-clockwise
  if (isPort) {
    while (sweep > 0) sweep -= 2 * Math.PI;
    if (sweep === 0) sweep = -2 * Math.PI;
  } else {
    while (sweep < 0) sweep += 2 * Math.PI;
    if (sweep === 0) sweep = 2 * Math.PI;
  }

  sweep *= Math.min(1, Math.max(0, sweepFraction));

  const steps = Math.max(8, Math.ceil(Math.abs(sweep) / (Math.PI / 16)));
  const stepAngle = sweep / steps;

  const points = [];
  for (let i = 0; i <= steps; i++) {
    const angle = startAngle + stepAngle * i;
    points.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
    });
  }

  if (points.length < 2) return '';

  let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L${points[i].x.toFixed(1)},${points[i].y.toFixed(1)}`;
  }
  return d;
}

// =========================================================================
// Classic mode (kept for toggle)
// =========================================================================

/** Nearest point on circle in direction of target (classic/simple tangent). */
export function tangentPoint(markPos, targetPos, radius) {
  const angle = Math.atan2(targetPos.y - markPos.y, targetPos.x - markPos.x);
  return {
    x: markPos.x + radius * Math.cos(angle),
    y: markPos.y + radius * Math.sin(angle),
  };
}

/** Classic rounding arc path (arrival-facing to departure-facing). */
export function roundingArcPath(prevPos, markPos, nextPos, isPort, radius, sweepFraction = 1) {
  const arrivalAngle = Math.atan2(prevPos.y - markPos.y, prevPos.x - markPos.x);
  const departAngle = Math.atan2(nextPos.y - markPos.y, nextPos.x - markPos.x);

  let sweep = departAngle - arrivalAngle;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  while (sweep < -Math.PI) sweep += 2 * Math.PI;

  if (isPort) {
    if (sweep > 0) sweep -= 2 * Math.PI;
  } else {
    if (sweep < 0) sweep += 2 * Math.PI;
  }

  sweep *= Math.min(1, Math.max(0, sweepFraction));

  const steps = Math.max(8, Math.ceil(Math.abs(sweep) / (Math.PI / 16)));
  const stepAngle = sweep / steps;

  const points = [];
  for (let i = 0; i <= steps; i++) {
    const angle = arrivalAngle + stepAngle * i;
    points.push({
      x: markPos.x + radius * Math.cos(angle),
      y: markPos.y + radius * Math.sin(angle),
    });
  }

  if (points.length < 2) return '';

  let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L${points[i].x.toFixed(1)},${points[i].y.toFixed(1)}`;
  }
  return d;
}

/** Classic arc radius: larger base, separate step per visit. */
export function arcRadius(visitNumber) {
  return 20 + (visitNumber - 1) * 10;
}

/** Classic per-visit position offsets for revisited marks. */
export function computeVisitPositions(positions, markIds) {
  const VISIT_OFFSET_PX = 8;
  const visitCount = {};
  const result = [];

  for (let i = 0; i < markIds.length; i++) {
    const id = markIds[i];
    const base = positions[id];
    if (!base) { result.push(null); continue; }

    visitCount[id] = (visitCount[id] || 0) + 1;
    const visit = visitCount[id];

    if (visit === 1) {
      result.push({ x: base.x, y: base.y });
    } else {
      const offsetDir = visit % 2 === 0 ? 1 : -1;
      const offsetMag = Math.ceil(visit / 2) * VISIT_OFFSET_PX;
      const prevPos = i > 0 ? positions[markIds[i - 1]] : null;
      const nextPos = i < markIds.length - 1 ? positions[markIds[i + 1]] : null;
      let offsetAngle;
      if (prevPos && nextPos) {
        const inAngle = Math.atan2(base.y - prevPos.y, base.x - prevPos.x);
        const outAngle = Math.atan2(nextPos.y - base.y, nextPos.x - base.x);
        offsetAngle = (inAngle + outAngle) / 2 + Math.PI / 2;
      } else if (prevPos) {
        offsetAngle = Math.atan2(base.y - prevPos.y, base.x - prevPos.x) + Math.PI / 2;
      } else if (nextPos) {
        offsetAngle = Math.atan2(nextPos.y - base.y, nextPos.x - base.x) + Math.PI / 2;
      } else {
        offsetAngle = 0;
      }
      result.push({
        x: base.x + offsetDir * offsetMag * Math.cos(offsetAngle),
        y: base.y + offsetDir * offsetMag * Math.sin(offsetAngle),
      });
    }
  }
  return result;
}

/**
 * Mark appearance string to fill color (matches CourseMap.markColor).
 */
export function markColor(appearance) {
  const a = (appearance || '').toLowerCase();
  if (a.includes('red') || a.startsWith('r')) return '#dc2626';
  if (a.includes('green') || a.startsWith('g')) return '#16a34a';
  if (a.includes('yellow') || a.startsWith('y')) return '#d97706';
  if (a.includes('black') || a.startsWith('b')) return '#1a1a2e';
  if (a.includes('orange')) return '#ea580c';
  return '#6b7280';
}
